import { Link } from 'react-router-dom';

import { BRAND_LOGO, BRAND_SYMBOL } from '@/shared/constants/app/brand.ts';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';

import { APP_HEADER } from './appHeader.constant';
import type { AppHeaderProps } from './appHeader.type';

/**
 * 앱 헤더 껍데기.
 *
 * 마케팅 헤더와 도움말 헤더가 **같은 높이·같은 배경·같은 로고 규칙**을 쓰도록
 * 공통부만 담는다. 메뉴 구성처럼 화면마다 다른 것은 슬롯으로 받는다 —
 * 여기에 도메인 분기(`if (isDocs)`)를 넣기 시작하면 두 헤더가 한 파일에서
 * 서로를 망가뜨린다.
 *
 * ── 세 존 ──────────────────────────────────────────────────
 *   좌: 로고 + `start`   중: `center`   우: `end`
 *
 * 좌측 존은 `startWidth` 로 셸의 사이드바 폭에 맞출 수 있고, 그때 `center` 가
 * 본문 열의 왼쪽 경계에서 정확히 시작한다. 예전에는 `center` 에 `mx-auto` 를
 * 걸어 **뷰포트 기준**으로 가운데를 잡았는데, 좌우 존 폭이 다르면 검색창이
 * 본문 열과 어긋난 채 떠 있었다.
 */
export function AppHeader({
  start,
  center,
  end,
  children,
  position = 'fixed',
  logoTo = PATHS.HOME,
  width = 'contained',
  startWidth = '',
  className = '',
}: AppHeaderProps) {
  return (
    <header
      className={[
        position === 'fixed' ? 'fixed inset-x-0 top-0' : 'sticky top-0',
        'z-50',
        className,
      ].join(' ')}
    >
      <nav className="relative border-b border-black/[0.08] bg-white/80 backdrop-blur-xl backdrop-saturate-150 dark:border-white/10 dark:bg-dark-base/80">
        <div
          className={`my-0.5 flex w-full ${APP_HEADER.height} items-center gap-4 ${APP_HEADER.gutter} ${
            width === 'full' ? '' : 'mx-auto max-w-[100rem]'
          }`}
        >
          <div className={`flex shrink-0 items-center gap-3 ${startWidth}`}>
            <AppHeaderLogo to={logoTo} />
            {start}
          </div>
          {/* 좁은 화면에서는 빠진다 — 자리를 반씩 나눠 가지면 둘 다 못 쓴다. */}
          {center && (
            <div className="hidden min-w-0 flex-1 lg:block">{center}</div>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
            {end}
          </div>
        </div>
        {children}
      </nav>
    </header>
  );
}

/** 모바일은 심볼, sm+ 는 풀 로고. 다크에서는 반전한다(단색 로고 전제). */
export function AppHeaderLogo({ to = PATHS.HOME }: { to?: string }) {
  return (
    <Link to={to} aria-label={BRAND_LOGO.name} className="flex items-center">
      <img
        className="h-8 w-auto sm:hidden dark:brightness-0 dark:invert"
        loading="lazy"
        alt={BRAND_SYMBOL.name}
        src={BRAND_SYMBOL.svg}
      />
      <img
        className="hidden h-8 w-auto sm:block dark:brightness-0 dark:invert"
        loading="lazy"
        alt={BRAND_LOGO.name}
        src={BRAND_LOGO.svg}
      />
    </Link>
  );
}
